import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';
import { Loader2, TrendingUp } from 'lucide-react';
import { salesApi, formatMoney, formatMoneyShort, serverError, SELLER_LABEL } from '../../lib/salesApi';
import SaleDetailModal from '../../components/sales/SaleDetailModal';
import ClientProfileDrawer from '../../components/sales/ClientProfileDrawer';
import { StatusPill } from '../../components/sales/SaleDetailModal';

const CY = '#90E0EF';

const RANGES = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 Days' },
  { key: '30d', label: '30 Days' },
];
const METRICS = [
  { key: 'sales_value', label: 'Sales Value' },
  { key: 'sales_count', label: 'Number of Sales' },
  { key: 'new_clients', label: 'New Clients' },
];

function rangeToDates(key) {
  const now = new Date();
  const to = now.toISOString();
  const from = new Date(now);
  if (key === 'today') { from.setHours(0, 0, 0, 0); }
  else if (key === '7d') { from.setDate(from.getDate() - 6); from.setHours(0, 0, 0, 0); }
  else { from.setDate(from.getDate() - 29); from.setHours(0, 0, 0, 0); }
  return { from: from.toISOString(), to };
}

function Kpi({ label, value, accent, testid }) {
  return (
    <div className="border border-[#1a1a1a] bg-[#080808] px-5 py-5" data-testid={testid}>
      <p className="font-outfit font-bold tracking-tight" style={{ fontSize: '2rem', lineHeight: 1, color: accent ? CY : '#ffffff' }}>{value}</p>
      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] mt-2">{label}</p>
    </div>
  );
}

const selectCls = 'bg-[#0d0d0d] border border-[#222] text-[#ddd] text-[10px] font-mono uppercase tracking-[0.1em] px-3 py-2 focus:outline-none focus:border-[#90E0EF]';

export default function SalesDashboardPage() {
  const ctx = useOutletContext();
  const [range, setRange] = useState('30d');
  const [metric, setMetric] = useState('sales_value');
  const [seller, setSeller] = useState('');
  const [saleType, setSaleType] = useState('');
  const [status, setStatus] = useState('');
  const [artistId, setArtistId] = useState('');
  const [artists, setArtists] = useState([]);
  const [dash, setDash] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tablesMissing, setTablesMissing] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [clientId, setClientId] = useState(null);

  useEffect(() => { salesApi.artists().then(setArtists).catch(() => {}); }, []);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const { from, to } = rangeToDates(range);
    const dashParams = { from, to, seller_scope: seller || undefined, sale_type: saleType || undefined, artist_id: artistId || undefined };
    const listParams = { seller_scope: seller || undefined, sale_type: saleType || undefined, status: status || undefined, artist_id: artistId || undefined };
    Promise.all([salesApi.dashboard(dashParams), salesApi.list(listParams)])
      .then(([d, l]) => { if (!cancel) { setDash(d); setSales(l.sales || []); setTablesMissing(false); } })
      .catch((e) => {
        if (!cancel) {
          if (e?.response?.status === 503) setTablesMissing(true);
          else toast.error(serverError(e));
        }
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [range, seller, saleType, status, artistId, ctx?.refreshKey]);

  const kpis = dash?.kpis;
  const chartData = useMemo(() => {
    const s = dash?.series || [];
    return s.map((b) => ({
      bucket: b.bucket,
      label: b.bucket.length > 10 ? b.bucket.slice(11) : b.bucket.slice(5),
      value: metric === 'sales_value' ? Math.round(b.sales_value / 100) : b[metric],
    }));
  }, [dash, metric]);

  if (tablesMissing) {
    return (
      <div className="border border-[#2a2416] bg-[#141207] px-6 py-8 text-center">
        <p className="font-outfit text-lg text-[#e4c07a]">Sales tables not initialised</p>
        <p className="text-xs font-mono text-[#aa9] mt-2 max-w-lg mx-auto">
          Run <span className="text-[#e4c07a]">/app/backend/migrations/2026_06_20_sales_os.sql</span> in the Supabase SQL Editor, then reload this page.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi label="Sales Value" accent value={loading ? '—' : formatMoneyShort(kpis?.sales_value_minor || 0, dash?.currency)} testid="kpi-sales-value" />
        <Kpi label="Sales" value={loading ? '—' : (kpis?.num_sales ?? 0)} testid="kpi-num-sales" />
        <Kpi label="Average Sale" value={loading ? '—' : formatMoneyShort(kpis?.average_sale_minor || 0, dash?.currency)} testid="kpi-avg-sale" />
        <Kpi label="New Clients" value={loading ? '—' : (kpis?.new_clients ?? 0)} testid="kpi-new-clients" />
      </div>

      {/* controls */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex border border-[#1a1a1a]">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)} data-testid={`range-${r.key}`} style={{ cursor: 'pointer' }}
              className="px-3 py-2 text-[10px] font-mono uppercase tracking-[0.14em] transition-colors">
              <span style={{ color: range === r.key ? CY : '#777' }}>{r.label}</span>
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <select className={selectCls} value={metric} onChange={(e) => setMetric(e.target.value)} style={{ cursor: 'pointer' }} data-testid="metric-select">
          {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      </div>

      {/* chart */}
      <div className="border border-[#1a1a1a] bg-[#080808] p-4 mb-6" data-testid="sales-chart">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-3.5 h-3.5" style={{ color: CY }} />
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#888]">{METRICS.find((m) => m.key === metric)?.label} over time</span>
        </div>
        <div style={{ width: '100%', height: 220 }}>
          {loading ? (
            <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin" style={{ color: CY }} /></div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#161616" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: '#555', fontSize: 9, fontFamily: 'JetBrains Mono' }} axisLine={{ stroke: '#222' }} tickLine={false} interval="preserveStartEnd" />
                <Tooltip
                  cursor={{ fill: 'rgba(144,224,239,0.06)' }}
                  contentStyle={{ background: '#0d0d0d', border: '1px solid #222', borderRadius: 0, fontFamily: 'JetBrains Mono', fontSize: 11 }}
                  labelStyle={{ color: '#888' }}
                  formatter={(v) => [metric === 'sales_value' ? formatMoneyShort(v * 100, dash?.currency) : v, METRICS.find((m) => m.key === metric)?.label]}
                />
                <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                  {chartData.map((_, i) => <Cell key={i} fill={CY} fillOpacity={0.75} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Breakdown title="By Seller" rows={dash?.breakdowns?.by_seller} fmt={(k) => SELLER_LABEL[k] || k} currency={dash?.currency} />
        <Breakdown title="By Type" rows={dash?.breakdowns?.by_type} fmt={(k) => k.toUpperCase()} currency={dash?.currency} />
        <Breakdown title="By Artist" rows={dash?.breakdowns?.by_artist} fmt={(k) => k} currency={dash?.currency} />
      </div>

      {/* filters + table */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] mr-2">Transactions</span>
        <select className={selectCls} value={seller} onChange={(e) => setSeller(e.target.value)} style={{ cursor: 'pointer' }} data-testid="filter-seller">
          <option value="">All Sellers</option>
          <option value="mr_relative">Mr. Relative</option>
          <option value="relative_gallery">The Relative Gallery</option>
        </select>
        <select className={selectCls} value={saleType} onChange={(e) => setSaleType(e.target.value)} style={{ cursor: 'pointer' }} data-testid="filter-type">
          <option value="">All Types</option>
          <option value="artwork">Artwork</option>
          <option value="card">Card</option>
          <option value="other">Other</option>
        </select>
        <select className={selectCls} value={status} onChange={(e) => setStatus(e.target.value)} style={{ cursor: 'pointer' }} data-testid="filter-status">
          <option value="">All Status</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="checkout_open">Checkout Open</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
          <option value="payment_conflict">Payment Conflict</option>
        </select>
        <select className={selectCls} value={artistId} onChange={(e) => setArtistId(e.target.value)} style={{ cursor: 'pointer' }} data-testid="filter-artist">
          <option value="">All Artists</option>
          {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>

      <div className="border border-[#1a1a1a] overflow-hidden" data-testid="sales-table">
        <div className="hidden md:grid grid-cols-[90px_70px_140px_1fr_120px_140px_110px] gap-2 px-4 py-2.5 border-b border-[#1a1a1a] bg-[#0a0a0a] text-[9px] font-mono uppercase tracking-[0.14em] text-[#666]">
          <span>Status</span><span>Time</span><span>Seller</span><span>Item / Artist</span><span>Edition</span><span>Client</span><span className="text-right">Price</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: CY }} /></div>
        ) : sales.length === 0 ? (
          <p className="px-4 py-8 text-center text-xs font-mono text-[#555]">No transactions yet.</p>
        ) : sales.map((s) => (
          <motion.button
            key={s.id} onClick={() => setDetailId(s.id)} whileHover={{ backgroundColor: 'rgba(144,224,239,0.03)' }}
            data-testid={`sale-row-${s.public_code}`} style={{ cursor: 'pointer' }}
            className="w-full text-left grid grid-cols-2 md:grid-cols-[90px_70px_140px_1fr_120px_140px_110px] gap-2 px-4 py-3 border-b border-[#141414] items-center"
          >
            <StatusPill status={s.status} />
            <span className="text-[10px] font-mono text-[#888] hidden md:block">{s.created_at ? new Date(s.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</span>
            <span className="text-[10px] font-mono text-[#aaa] hidden md:block truncate">{s.seller_label}</span>
            <span className="text-sm text-[#e8e8e8] truncate">{s.item_title}<span className="text-[#666] text-xs"> · {s.artist_name || '—'}</span></span>
            <span className="text-xs font-mono text-[#888] hidden md:block">{s.edition || (s.card_number_display ? `#${s.card_number_display}` : '—')}</span>
            <span className="text-xs font-mono text-[#aaa] hidden md:block truncate">{s.buyer_name || s.buyer_email}</span>
            <span className="font-mono text-sm text-white text-right">{formatMoney(s.amount_minor, s.currency)}</span>
          </motion.button>
        ))}
      </div>

      <SaleDetailModal saleId={detailId} open={!!detailId} onClose={() => setDetailId(null)} onChanged={() => salesApi.list({}).then((l) => setSales(l.sales || [])).catch(() => {})} onViewClient={(cid) => { setDetailId(null); setClientId(cid); }} />
      <ClientProfileDrawer clientId={clientId} open={!!clientId} onClose={() => setClientId(null)} />
    </div>
  );
}

function Breakdown({ title, rows, fmt, currency }) {
  const data = rows || [];
  const max = Math.max(1, ...data.map((r) => r.value || 0));
  return (
    <div className="border border-[#1a1a1a] bg-[#080808] p-4">
      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] mb-3">{title}</p>
      {data.length === 0 ? <p className="text-xs font-mono text-[#555]">No paid sales.</p> : (
        <div className="space-y-2.5">
          {data.slice(0, 6).map((r) => (
            <div key={r.key}>
              <div className="flex items-center justify-between text-[11px] font-mono">
                <span className="text-[#ccc] truncate mr-2">{fmt(r.key)}</span>
                <span className="text-white shrink-0">{formatMoneyShort(r.value, currency)}</span>
              </div>
              <div className="mt-1 h-1 bg-[#161616]"><div className="h-1" style={{ width: `${(r.value / max) * 100}%`, background: CY, opacity: 0.7 }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
